/**
 * The business's Bookings setup, for the Book step's inspector (7 October 2026): the services
 * (`GET /v1/bookings/services`) and the people (`GET /v1/bookings/staff`) from the bookings service, as
 * `_docs/modules/bookings-contract.md` describes them. Typed here, narrowly, because the inspector reads only a
 * few fields; the full shapes belong to the Bookings pages.
 *
 * `failed` is true when the services cannot be read (the console routes are not up, or bookings is not set up
 * for this organisation): the inspector then says "Set up bookings first" rather than showing empty pickers.
 */
import { useQuery } from '@tanstack/react-query';
import { http, type Envelope } from '@repletoryreach/sdk-api';

export interface BookingQuestionSpec {
  key: string;
  label: string;
  type: string;
  required?: boolean;
  options?: string[];
}

export interface BookingServiceSpec {
  id: string;
  name: string;
  kind?: string;
  staff_mode?: string;
  questions?: BookingQuestionSpec[];
  staff_ids?: string[];
  active?: boolean;
}

export interface BookingStaffSpec {
  id: string;
  name: string;
  active?: boolean;
}

export function useBookingSetup() {
  const services = useQuery({
    queryKey: ['bookings', 'services'],
    queryFn: () => http.get<Envelope<BookingServiceSpec[]>>('/v1/bookings/services'),
    staleTime: 60_000,
    retry: false
  });
  const staff = useQuery({
    queryKey: ['bookings', 'staff'],
    queryFn: () => http.get<Envelope<BookingStaffSpec[]>>('/v1/bookings/staff'),
    staleTime: 60_000,
    retry: false
  });
  const list = Array.isArray(services.data?.data) ? services.data.data : [];
  const people = Array.isArray(staff.data?.data) ? staff.data.data : [];
  return {
    pending: services.isPending,
    failed: services.isError,
    services: list.filter((s) => s.active !== false),
    staff: people.filter((p) => p.active !== false)
  };
}
